import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import Quagga, { QuaggaJSResultObject } from "@ericblade/quagga2";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../Context/Contexts";

interface ScannerDialogProps {
  open: boolean;
  onClose: () => void;
  onScan: (barcode: string) => Promise<boolean> | boolean;
}

const SCAN_INTERVAL = 50;
const REQUIRED_MATCHES = 3;

const ScannerDialog: React.FC<ScannerDialogProps> = ({ open, onClose, onScan }) => {
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const scanLockRef = useRef(false);
  const lastScanTimeRef = useRef(0);
  const previousCodeRef = useRef<string | null>(null);
  const codeMatchCountRef = useRef(0);
  const onScanRef = useRef(onScan);

  const [scannedCode, setScannedCode] = useState("");
  const [isScanLocked, setIsScanLocked] = useState(false);

  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  const unlockScanner = useCallback(() => {
    scanLockRef.current = false;
    setIsScanLocked(false);
    previousCodeRef.current = null;
    codeMatchCountRef.current = 0;
    lastScanTimeRef.current = 0;
  }, []);

  const resetScannerState = useCallback(() => {
    unlockScanner();
    setScannedCode("");
  }, [unlockScanner]);

  const handleDialogClose = useCallback(() => {
    resetScannerState();
    onClose();
  }, [onClose, resetScannerState]);

  const handleScanAgain = useCallback(() => {
    resetScannerState();
  }, [resetScannerState]);

  const onDetected = useCallback(
    (result: QuaggaJSResultObject) => {
      if (scanLockRef.current) {
        return;
      }

      const now = Date.now();
      if (now - lastScanTimeRef.current < SCAN_INTERVAL) {
        return;
      }

      lastScanTimeRef.current = now;
      const code = result?.codeResult?.code;
      if (!code) {
        return;
      }

      setScannedCode(code);

      if (code === previousCodeRef.current) {
        codeMatchCountRef.current += 1;
      } else {
        previousCodeRef.current = code;
        codeMatchCountRef.current = 1;
      }

      if (codeMatchCountRef.current >= REQUIRED_MATCHES) {
        scanLockRef.current = true;
        setIsScanLocked(true);

        Promise.resolve(onScanRef.current(code))
          .then((wasSuccessful) => {
            if (!wasSuccessful) {
              unlockScanner();
            }
          })
          .catch(() => {
            unlockScanner();
          });
      }
    },
    [unlockScanner]
  );

  useEffect(() => {
    if (!open) {
      resetScannerState();
      return;
    }

    let waitForRef: ReturnType<typeof setInterval> | null = null;

    const startScanner = () => {
      if (!videoContainerRef.current) {
        return;
      }

      Quagga.init(
        {
          inputStream: {
            name: "Live",
            type: "LiveStream",
            target: videoContainerRef.current,
            constraints: {
              facingMode: "environment",
              width: { ideal: 640 },
              height: { ideal: 480 },
            },
          },
          locator: {
            patchSize: "medium",
            halfSample: false,
          },
          decoder: {
            readers: ["code_128_reader", "ean_reader", "ean_8_reader"],
            multiple: false,
          },
          locate: true,
          numOfWorkers: 2,
          frequency: 20,
        },
        (err :any) => {
          if (err) {
            console.error("Quagga init failed:", err);
            showSnackbar(
              `${t("dialog.failedToStartScanner", { defaultValue: "Failed to start scanner" })} : ${err?.message ?? err}`,
              "error"
            );
            handleDialogClose();
            return;
          }

          Quagga.start();
          Quagga.offDetected(onDetected);
          Quagga.onDetected(onDetected);
        }
      );
    };

    waitForRef = setInterval(() => {
      if (videoContainerRef.current) {
        if (waitForRef) {
          clearInterval(waitForRef);
        }
        startScanner();
      }
    }, 100);

    return () => {
      if (waitForRef) {
        clearInterval(waitForRef);
      }

      Quagga.offDetected(onDetected);
      try {
        Quagga.stop();
      } catch (error) {
        console.error("Failed to stop Quagga:", error);
      }

      resetScannerState();
    };
  }, [open, onDetected, resetScannerState, showSnackbar, t, handleDialogClose]);

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("common.scanBarcode", { defaultValue: "Scan barcode" })}</DialogTitle>
      <DialogContent>
        <Box
          ref={videoContainerRef}
          sx={{
            width: "100%",
            maxWidth: { xs: "90vw", sm: "100%" },
            aspectRatio: "4 / 3",
            borderRadius: 2,
            border: "1px solid #ccc",
            overflow: "hidden",
          }}
        />
        <Typography variant="subtitle1" sx={{ mt: 2, minHeight: 32 }}>
          {scannedCode}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleScanAgain} disabled={!isScanLocked} variant="contained" color="success">
          {isScanLocked
            ? t("common.scanAgain")
            : t("common.scanningdotdot")}
        </Button>
        <Button onClick={handleDialogClose} variant="contained">{t("common.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScannerDialog;
